import { useState, useEffect } from 'react'
import Modal from 'views/components/UI/Modal'
import { H4, H5, P } from 'views/components/UI/Text'
import { BackButton, Button, StrokedButton } from 'views/components/UI/Buttons'
import DeleteIcon from 'assets/icons/DeleteIcon'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useNotification } from 'lib/context/notification.context'
import {
  ButtonsContainer,
  DeleteButton,
  ModalContent,
  ModalHeader,
  QuestionItem,
  QuestionNumber,
  QuestionsContainer,
  TextArea,
  TextAreaContainer,
  CharCounter,
  TitleContainer,
} from './styled'

export interface ModalQuestionsProps {
  showModal: boolean
  handleClose: () => void
  skillName: string
  questions: string[]
  onAddQuestion: (question: string) => void
  onRemoveQuestion: (index: number) => void
  onUpdateQuestion?: (index: number, value: string) => void
  skillIndex: number
  onCancel?: () => void
}

const ModalQuestions: React.FC<ModalQuestionsProps> = ({
  showModal,
  handleClose,
  skillName,
  questions,
  onAddQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
  skillIndex,
  onCancel,
}) => {
  const [editedQuestions, setEditedQuestions] = useState<string[]>([])
  const [newQuestion, setNewQuestion] = useState<string>('')
  const { getWarning } = useNotification()

  useEffect(() => {
    if (showModal) {
      const questionsCopy = JSON.parse(JSON.stringify(questions))
      setEditedQuestions(questionsCopy)
      setNewQuestion('')
    }
  }, [questions, showModal])

  const handleAddQuestion = () => {
    if (newQuestion.trim()) {
      if (editedQuestions.length >= 6) {
        getWarning('No se pueden agregar más de 6 items por competencia')
        return
      }

      onAddQuestion(newQuestion.trim())

      setEditedQuestions([...editedQuestions, newQuestion.trim()])

      setNewQuestion('')
    }
  }

  const handleQuestionChange = (index: number, value: string) => {
    const updatedQuestions = [...editedQuestions]
    updatedQuestions[index] = value
    setEditedQuestions(updatedQuestions)

    if (onUpdateQuestion) {
      onUpdateQuestion(index, value)
    }
  }

  const handleCancelModal = () => {
    setEditedQuestions([])
    setNewQuestion('')

    if (onCancel) {
      console.log('Modal: Llamando a onCancel para restaurar estado en el padre')
      onCancel()
    } else {
      console.log('Modal: No hay función onCancel disponible')
    }

    handleClose()
  }

  const handleDisabledClick = () => {
    if (editedQuestions.length >= 5) {
      getWarning('No se pueden agregar más de 6 items por competencia')
    }
  }

  const isContinueButtonDisabled = () => {
    return editedQuestions.length === 0 && !newQuestion.trim()
  }

  return (
    <Modal
      show={showModal}
      setShow={handleClose}
      hideCloseButton
      styleContent={{
        maxWidth: '90vw',
        maxHeight: 'auto',
        padding: '20px',
        margin: '0',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <H4>Preguntas o comportamientos a evaluar</H4>
          <H5 style={{ fontWeight: '400' }}>
            Ingresa la información de las preguntas o comportamientos que permitirán evaluar el desempeño de las
            personas en cada competencia. Puedes asignar hasta 6 preguntas por competencia.{' '}
          </H5>
        </ModalHeader>
        <TitleContainer style={{ backgroundColor: '#ffffff' }}>
          <H5 style={{ fontWeight: 'normal' }} margin="0">
            0{skillIndex + 1}.
          </H5>
          <H4>{skillName}</H4>
        </TitleContainer>

        <QuestionsContainer>
          {editedQuestions.map((question, index) => (
            <QuestionItem key={`question-item-${index}`}>
              <QuestionNumber>
                <P margin="10px 0 0 0">
                  {skillIndex + 1}.{index + 1}
                </P>
                <TextAreaContainer>
                  <TextArea
                    placeholder="Descripción del comportamiento o pregunta a evaluar"
                    value={question}
                    onChange={(e) => handleQuestionChange(index, e.target.value)}
                    maxLength={250}
                  />
                  <CharCounter isExceeded={question.length > 250}>{question.length}/250</CharCounter>
                </TextAreaContainer>
              </QuestionNumber>
              <DeleteButton
                onClick={() => {
                  // Primero actualizar el estado local
                  const updatedQuestions = [...editedQuestions]
                  updatedQuestions.splice(index, 1)
                  setEditedQuestions(updatedQuestions)

                  // Luego notificar al padre
                  onRemoveQuestion(index)
                  console.log('Modal: Pregunta eliminada en índice', index)
                }}
              >
                <DeleteIcon />
                Eliminar
              </DeleteButton>
            </QuestionItem>
          ))}

          {editedQuestions.length < 6 && (
            <QuestionItem>
              <QuestionNumber>
                <P margin="10px 0 0 0">
                  {skillIndex + 1}.{editedQuestions.length + 1}
                </P>
                <TextAreaContainer>
                  <TextArea
                    placeholder="Descripción del comportamiento o pregunta a evaluar"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    maxLength={250}
                  />
                  <CharCounter isExceeded={newQuestion.length > 250}>{newQuestion.length}/250</CharCounter>
                </TextAreaContainer>
              </QuestionNumber>
            </QuestionItem>
          )}
        </QuestionsContainer>

        <StrokedButton
          onClick={handleAddQuestion}
          onDisabledClick={handleDisabledClick}
          type="button"
          disabled={editedQuestions.length >= 5 || !newQuestion.trim()}
          style={{ margin: '20px 0 0 60px', display: 'flex', alignItems: 'center', width: 'fit-content' }}
        >
          <AddCircleOutlineIcon
            style={{
              marginRight: '5px',
              color: editedQuestions.length >= 5 || !newQuestion.trim() ? '#C2C2C2' : '#2D749C',
            }}
          />
          Añadir pregunta
        </StrokedButton>

        <ButtonsContainer>
          <BackButton text="Cancelar" hideIcon onClick={handleCancelModal} />
          <Button
            onClick={() => {
              // Verificar si hay contenido en newQuestion y agregarlo si es necesario
              if (newQuestion.trim()) {
                onAddQuestion(newQuestion.trim())
              }

              handleClose()
            }}
            disabled={isContinueButtonDisabled()}
            type="button"
          >
            Continuar
          </Button>
        </ButtonsContainer>
      </ModalContent>
    </Modal>
  )
}

export default ModalQuestions
